import React, { useEffect, useMemo, useState } from "react";
import { Button, Field, Groupper, Input, Message, Header, Table, Toolbar, Checkbox, Modal, Icon} from 'react-frontier2';
import { SetLoading, SystemAccess, SystemAccessTypes } from '@arema/components/Classes';
import { UserClass } from '../../AdminClasses';
import { bindClick, bindFormChange, partition } from "@arema/components/Util";
import { Link, useParams } from "react-router-dom";
import { NotFound, SystemAccessRow } from "../../components";
import { useUser } from "../../AdminHooks";
import { useTranslation } from "react-i18next";
import API from "../../API";
import Validator from "@arema/components/Validator";
import UserAccess from "../../UserAccess";
import moment from "moment";
import classNames from "classnames";
import { useFrontier } from "react-frontier2";

interface AccessCategory{
	category_id: number,
	category_name: string,
	system_id: number,
	sort_order: number,
	access: {
		access_id: number,
		access_name: string,
		restricted: boolean
	}[]
}

enum PASSWORD_MODE{
	AUTOGENERATED = 0,
	PROVIDED = 1,
}

enum Modals{
	CHANGE_PASSWORD = 1,
	CREATION_TABLE = 2,
	CLONE_USER = 3,
	REFRESH_TOKEN = 4,
	DEACTIVATE_USER = 5,
	GRANT_SYSTEM_ACCESS = 6,
	SYSTEM_ACCESS_INFO = 7,
	PASSWORD_LINK = 8,
}

interface UserClone {
	email: string,
	first_name: string,
	last_name: string,
}

interface UserDetailsProps{
	onUserData: (v: UserClass)=>void
}

type UserSystems = {
	[P in SystemAccessTypes]: {
		system_id: number,
		user_added: number,
		user_added_name: string,
		date_added: number,
	}
}

var UserDetails = (props: UserDetailsProps)=>{
	var params = useParams<{ id: string }>();
	var { t } = useTranslation();
	var { user: loggedUser, hasAccess } = useUser();
	var { Toast } = useFrontier();
	var [user, setUser] = useState<UserClass>(null);
	var [access, setAccess] = useState<number[]>(null);
	var [allAccess, setAllAccess] = useState<AccessCategory[]>(null);
	var [systems, setSystems] = useState<UserSystems>(null);
	var [formPrompts, setFormPrompts] = useState<string[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [userCreator, setUserCreator] = useState<{ user_id: number, first_name: string, last_name: string }>(null);
	var [passwordForm, setPasswordForm] = useState<{ mode: PASSWORD_MODE, password: string }>(null);
	var [userCloneForm, setUserCloneForm] = useState<UserClone>(null);
	var [passwordLink, setPasswordLink] = useState<string>(null);
	var [modalConfirm, setModalConfirm] = useState<boolean>(false);
	var [editingSystem, setEditingSystem] = useState<SystemAccessTypes>(null);
	var [search, setSearch] = useState<string>('');

	var user_id = (user && user.user_id) ? user.user_id : ((params && params.id && !Number.isNaN(parseInt(params.id))) ? parseInt(params.id) : null);

	useEffect(()=>{
		if(user_id){
			API.getUser(user_id, {
				access: true,
				all_access: true,
				systems: true
			}).then(res=>{
				if(res.error) return setLoadError(res.message);
				setUser(res.data.user);
				setAccess(res.data.access);
				setAllAccess(res.data.all_access);
				setSystems(res.data.systems);
				if(props.onUserData) props.onUserData(res.data.user);
			}).catch(err=>{
				return setLoadError('Hubo un error ineseprado cargando el usuario.');
			});
		}
	}, [params.id]);

	useEffect(()=>{
		setModalConfirm(false);
		setModalPrompts(null);
	}, [shownModal]);

	var {
		access_list,
		system_access,
	} = useMemo(()=>{
		var system_access : number[] = [];
		var access : typeof allAccess = [];
		if(user?.superadmin || !allAccess || !systems) return { system_access, access_list: access }

		for(var i in systems){
			if(!systems[i as SystemAccessTypes]) continue;
			system_access.push(systems[i as SystemAccessTypes].system_id);
		}

		if(!search || search.length<2){
			access = [...allAccess];
		}else{
			for(var ac of allAccess){
				var al = ac.access.filter(a=>a.access_name.trim().toLowerCase().indexOf(search.trim().toLowerCase())>-1);
				access.push({
					...ac,
					access: al
				});
			}
		}

		return {
			access_list: access.filter(a=>(
				a.access.length>0 && 
				(a.system_id===null || system_access.indexOf(a.system_id)!=-1)
			)).sort((a,b)=>a.sort_order-b.sort_order),
			system_access,
		}
	}, [allAccess, systems, search]);

	if(!user_id){
		return <NotFound />
	}

	if(!user || loadError) return (
		<Header 
			loading={!loadError} 
			text={loadError ? 'Error' :  "Cargando usuario"} 
			subtext={loadError} 
			iconName={loadError ? "face-frown-open" : null} 
		/>
	)

	var submit = (setLoading: SetLoading)=>{
		if(!canEdit) return;
		var { valid, prompts } = Validator(user, {
			email: [{
				rule: 'email', prompt: 'El correo electrónico no es válido'
			}],
			first_name: [{
				rule: 'length', params: [3, 64], label: 'Nombre'
			}],
			last_name: [{
				rule: 'length', params: [3, 64], label: 'Apellido'
			}],
		});
		setFormPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.editUser(user_id, {
			email: user.email,
			first_name: user.first_name,
			last_name: user.last_name,
			active: user.active,
		}).then(res=>{
			if(res.error) return setFormPrompts([res.message]);
			Toast.success('Se ha editado el usuario.');
		}).catch(err=>{
			return setFormPrompts(['Hubo un error inesperado editando el usuario (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var saveAccess = (setLoading: SetLoading)=>{
		if(!hasAccess(UserAccess.USERS.EDIT_ACCESS)) return;
		setLoading(true);
		API.saveAccess(user_id, access).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Se han editado los permisos del usuario.');
		}).catch(err=>{
			Toast.error('Hubo un error inesperado editando los permisos del usuario (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		})
	}

	var changePassword = ()=>{
		if(!canChangePassword) return;
		setPasswordForm({
			mode: PASSWORD_MODE.AUTOGENERATED,
			password: ''
		});
		setShownModal(Modals.CHANGE_PASSWORD);
	}

	function arrangeAccess<T>(access: T[]){
		return partition(access, 2);
	}

	var onAccessChange = (access_id: number)=>{
		return (val: boolean)=>{
			var acc = [...access];
			acc = acc.filter(a=>a!=access_id);
			if(val) acc.push(access_id);
			setAccess(acc);
		}
	}

	var setCategoryAccess = (category_id: number)=>{
		return (val: boolean)=>{
			var cat = allAccess.find(a=>a.category_id==category_id);
			if(!cat) return false;
			var acc = [...access];
			var cat_acc = cat.access.map(a=>a.access_id);
			acc = acc.filter(a=>cat_acc.indexOf(a)==-1);
			if(val) acc.push(...cat.access.filter(a=>!a.restricted).map(a=>a.access_id));
			setAccess(acc);
		}
	}

	var isCategoryComplete = (category_id: number)=>{
		var cat = allAccess.find(a=>a.category_id==category_id);
		if(!cat) return false;
		var cat_acc = [...cat.access];
		if(!loggedUser.superadmin){
			cat_acc = cat_acc.filter(a=>!a.restricted)
		}
		for(var i of cat_acc.map(a=>a.access_id)){
			if(access.indexOf(i)==-1) return false;
		}
		return true;
	}

	var sendPasswordChange = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(passwordForm, {
			mode: ['number'],
			password: passwordForm.mode===PASSWORD_MODE.AUTOGENERATED ? [] : [
				{ rule: 'password' }
			]
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.changeUserPassword(user.user_id, passwordForm.mode===PASSWORD_MODE.AUTOGENERATED, passwordForm.password).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha cambiado la contraseña del usuario y se le ha enviado por correo.');
			setPasswordForm(null);
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado cambiando la contraseña (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var showPasswordLinkModal = ()=>{
		setShownModal(Modals.PASSWORD_LINK);
		setModalPrompts(null);
		if(!passwordLink){
			API.getResetPasswordLink(user.user_id).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				setPasswordLink(res.data.url);
			}).catch(err=>{
				setModalPrompts([t('common.unexpected_code', { code: `LCL-USRDT-SWPWLK-1` })])
			});
		}
	}

	var showCreationModal = ()=>{
		setShownModal(Modals.CREATION_TABLE);
		setModalPrompts([]);
		if(!userCreator){
			if(!user.user_creator) return;
			API.getUsers([user.user_creator]).then(res=>{
				if(res.error) return;
				setUserCreator(res.data[0]);
			}).catch(err=>{});
		}
	}

	var showCloneModal = ()=>{
		setShownModal(Modals.CLONE_USER);
		setModalPrompts([]);
		setUserCloneForm({
			email: '',
			first_name: '',
			last_name: '',
		})
	}

	var showRefreshModal = ()=>{
		setShownModal(Modals.REFRESH_TOKEN);
		setModalConfirm(false);
		setModalPrompts([]);
	}
	
	// var saveClone = (setLoading: SetLoading ) => {
	// 	var { valid, prompts } = Validator(userCloneForm, {
	// 		username: [{
	// 			rule: /^[0-9a-zA-Z]{4,32}$/, prompt: 'El nombre de usuario no es válido',
	// 		}],
	// 		email: [{
	// 			rule: 'email', prompt: 'El correo electrónico no es válido'
	// 		}],
	// 		first_name: [{
	// 			rule: 'length', params: [3, 64], label: 'Nombre'
	// 		}],
	// 		last_name: [{
	// 			rule: 'length', params: [3, 64], label: 'Apellido'
	// 		}],
	// 	});
	// 	if(!valid) return Toast.error(prompts.join());
	// 	setLoading(true);
	// 	API.cloneUser({
	// 		email: userCloneForm.email,
	// 		first_name: userCloneForm.first_name,
	// 		last_name: userCloneForm.last_name,
	// 		id_original_user: user.user_id
	// 	}).then(res=>{
	// 		if(res.error) return Toast.error(res.message);
	// 		setShownModal(null);
	// 		setUser(null);
	// 		Toast.success('Se ha clonado el usuario');
	// 		navigate(`/admins/${res.data.admin_id}`);
	// 	}).catch(err=>{
	// 		return Toast.error('Hubo un error inesperado al clonar el usuario (LCL-1)');
	// 	}).finally(()=>{
	// 		setLoading(false);
	// 	});
	// }

	var sendReinitialize = (setLoading: SetLoading)=>{
		setLoading(true);
		API.refreshUserSecret(user.user_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha reinicializado la sesión del usuario');
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado reinicializando el usuario (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var hasSystemGrantAccess = (system: SystemAccessTypes)=>{
		var acc = UserAccess.USERS[`GIVE_${system}_ACCESS`];
		if(!acc) return false;
		return hasAccess(acc);
		// switch(system){
		// 	case SystemAccess.ADMIN: return hasAccess(UserAccess.USERS.GIVE_ADMIN_ACCESS);
		// 	case SystemAccess.OPERATIONS: return hasAccess(UserAccess.USERS.GIVE_OPERATIONS_ACCESS);
		// 	case SystemAccess.PDV: return hasAccess(UserAccess.USERS.GIVE_PDV_ACCESS);
		// 	case SystemAccess.REPORTS: return hasAccess(UserAccess.USERS.GIVE_REPORTS_ACCESS);
		// 	case SystemAccess.SCANNER: return hasAccess(UserAccess.USERS.GIVE_SCANNER_ACCESS);
		// 	default: return false;
		// }
	}

	var setUserSystem = (system: SystemAccessTypes)=>{
		return (val: boolean)=>{
			if(!hasSystemGrantAccess(system)) return Toast.error('No tienes permiso para cambiar el acceso de este sistema del usuario');
			setEditingSystem(system);
			setShownModal(Modals.GRANT_SYSTEM_ACCESS);
		}
	}

	var sendGrantAccess = (system: SystemAccessTypes)=>{
		return (setLoading: SetLoading)=>{
			if(!hasSystemGrantAccess(system)) return;
			var system_id = SystemAccess[system];
			if(!system_id) return;

			var has_access = !!systems[system];
			setLoading(true);
			API.updateUserSystemAccess(user_id, system_id, !has_access).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				var sys = {...systems};
				if(has_access){
					sys[system] = null;
				}else{
					sys[system] = {
						system_id,
						user_added: loggedUser.user_id,
						user_added_name: `${loggedUser.first_name} ${loggedUser.last_name}`,
						date_added: moment().unix(),
					}
				}
				setSystems(sys);
				setShownModal(null);
				Toast.success(`Se le ha ${has_access ? 'quitado' : 'agregado'} al usuario el acceso al sistema`);
			}).catch(err=>{
				setModalPrompts(['Hubo un error inesperado cambiando el acceso del usuario (LCL-UDSGA-1)']);
			}).finally(()=>{
				setLoading(false);
			})
		}
	}

	var viewSystemInfo = (system: SystemAccessTypes)=>{
		return ()=>{
			setEditingSystem(system);
			setShownModal(Modals.SYSTEM_ACCESS_INFO);
		}
	}

	var sendActivate = (setLoading: SetLoading)=>{

	}

	var onUserChange = bindFormChange(user, setUser);
	var onPassChange = bindFormChange(passwordForm, setPasswordForm);
	// var onUserCloneChange = bindFormChange(userCloneForm, setUserCloneForm);
	var is_dev = hasAccess(UserAccess.ADMIN.DEVELOPER);
	var canEdit = hasAccess(UserAccess.USERS.EDIT_USERS);
	var canChangePassword = hasAccess(UserAccess.USERS.CHANGE_PASSWORD) && user && (!user.superadmin || loggedUser.superadmin);
	var canActivate = hasAccess(UserAccess.USERS.DEACTIVATE_USER);
	var canRefresh = hasAccess(UserAccess.USERS.REFRESH_USER);
	var canCreate = hasAccess(UserAccess.USERS.CREATE_USERS);

	return <div className="ar user-details-screen">
		{!user.active && (
			<Message 
				centered
				type="error"
				header="Usuario desactivado"
				text="Usuario desactivado por administrador"
				style={{ maxWidth: 600, margin: 'auto', marginBottom: 15 }}
			/>
		)}
		<Groupper style={{ maxWidth: 600 }} title="Usuario" titleRight={`ID: ${user.user_id}`} actions={canEdit && (
			<Button text="Guardar" color="black" onClick={submit} />
		)}>
			<Toolbar fitted>
				{(canChangePassword || (canEdit && canActivate && !!user.active && !user.superadmin)) && (
					<Toolbar.Dropdown text="Editar" iconName="pencil" items={[
						{ text: "Cambiar contraseña", onClick: changePassword, if: canChangePassword },
						// { text: "Desactivar usuario", style: { color: 'brown' }, onClick: bindClick(setShownModal, Modals.DEACTIVATE_USER), if: canEdit && canActivate && !!user.active && !user.superadmin }
					]} />
				)}
				<Toolbar.Dropdown text="Herramientas" iconName="wrench" items={[
					{ text: 'Ver creación', onClick: showCreationModal },
					{ text: 'Ver link de contraseña', onClick: showPasswordLinkModal },
					{ text: 'Reiniciar sesión', onClick: showRefreshModal, if: canRefresh },
				]} />
			</Toolbar>
			{/* <Input label="Usuario" placeholder="Nombre de usuario" value={user.username} readonly={!canEdit} onChange={onUserChange('username')} /> */}
			<Field amount={2}>
				<Input label="Nombre" readonly={!canEdit} value={user.first_name} onChange={onUserChange('first_name')} />
				<Input label="Apellido" readonly={!canEdit} value={user.last_name} onChange={onUserChange('last_name')} />
			</Field>
			<Input label="Correo electrónico" value={user.email} readonly={!canEdit} onChange={onUserChange('email')} />
			<Message type="error" list={formPrompts} />
		</Groupper>
		{!user.superadmin && (
			<Groupper style={{ marginTop: 15, maxWidth: 600 }} title={'Accesos'}>
				<Table className="bottom" striped fitted top last>
					<Table.Body>
						<SystemAccessRow system="ADMIN" onChange={setUserSystem('ADMIN')} value={!!systems?.ADMIN} action={!!systems?.ADMIN && (
							<Button size="tiny" iconName="info-circle" icon onClick={viewSystemInfo('ADMIN')} />
						)} />
						<SystemAccessRow system="OPERATIONS" onChange={setUserSystem('OPERATIONS')} value={!!systems?.OPERATIONS} action={!!systems?.OPERATIONS && (
							<Button size="tiny" iconName="info-circle" icon onClick={viewSystemInfo('OPERATIONS')} />
						)} />
						<SystemAccessRow system="PDV" onChange={setUserSystem('PDV')} value={!!systems?.PDV} action={!!systems?.PDV && (
							<Button size="tiny" iconName="info-circle" icon onClick={viewSystemInfo('PDV')} />
						)} />
						<SystemAccessRow system="REPORTS" onChange={setUserSystem('REPORTS')} value={!!systems?.REPORTS}  action={!!systems?.REPORTS && (
							<Button size="tiny" iconName="info-circle" icon onClick={viewSystemInfo('REPORTS')} />
						)}/>
						<SystemAccessRow system="SCANNER" onChange={setUserSystem('SCANNER')} value={!!systems?.SCANNER} action={!!systems?.SCANNER && (
							<Button size="tiny" iconName="info-circle" icon onClick={viewSystemInfo('SCANNER')} />
						)} />
					</Table.Body>
				</Table>
			</Groupper>
		)}
		{(hasAccess(UserAccess.USERS.EDIT_ACCESS) && !user.superadmin && system_access.length>0) && (
			<Groupper fitted style={{ marginTop: 15, maxWidth: 600 }} title="Permisos" actions={(
				<Button text="Guardar" color="black" onClick={saveAccess} />
			)}>
				<Groupper.DividerInput removeBorder={access_list.length===0 ? ['top'] : ['top', 'bottom']} removeMargin={['top', 'bottom']} placeholder={'Buscar permisos'} value={search} onChange={setSearch} />
				{access_list.length===0 && search.length>0 && system_access.length>0 && (
					<Header text={'Sin resultados'} subtext={`No se encontraron permisos con "${search.toLowerCase()}"`} style={{ marginTop: 20 }} />
				)}
				{access_list.map(a=>(
					<React.Fragment key={`acc-${a.category_id}`}>
						<Groupper.Divider style={{ paddingLeft: 10, paddingRight: 10, display: 'flex', alignItems: 'center', padding: 10 }}>
							<Checkbox className="normal" onChange={setCategoryAccess(a.category_id)} checked={isCategoryComplete(a.category_id)} />
							<div className="name" style={{ marginLeft: 10, fontWeight: 'bold', fontSize: 16 }}>{a.category_name}</div>
						</Groupper.Divider>
						<table className="access" style={{ marginTop: 15, width: '100%' }}>
							<tbody>
								{arrangeAccess(a.access).map((b, i)=>(
									<tr key={`ACCTB-${a.category_id}-${i}`}>
										{b.map(acc=>(
											<td style={{ width: '50%' }} key={`ACCTB-${a.category_id}-${acc.access_id}`}>
												<div className="access">
													<Checkbox color={acc.restricted ? 'red' : 'blue'} checked={access.indexOf(acc.access_id)!=-1} onChange={onAccessChange(acc.access_id)} className="normal" />
													{is_dev && (
														<div className="id">[{acc.access_id}]</div>
													)}
													<div className={classNames('name', {
														restricted: acc.restricted
													})}>
														{acc.access_name}
													</div>
												</div>
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
						<div style={{ marginBottom: 25 }} />
					</React.Fragment>
				))}
				<div style={{ padding: 15, marginTop: 15 }}>
					<Message type="info" text={`El usuario tiene ${access ? access.length : 0} permiso${access && access.length==1 ? '' : 's'}.`} />
				</div>
			</Groupper>
		)}
		{hasAccess(UserAccess.USERS.EDIT_ACCESS) && !!user.superadmin && (
			<Groupper title="Permisos" style={{ marginTop: 15, maxWidth: 600 }}>
				<Header text="Usuario superadmin" subtext="Este usuario es un superadmin, ya puede hacer todo en el sistema." />
			</Groupper>
		)}
		{!!canChangePassword && (
			<Modal open={shownModal===Modals.CHANGE_PASSWORD} onClose={bindClick(setShownModal)} size="mini">
				<Modal.Header>Cambiar contraseña</Modal.Header>
				<Modal.Content>
					<Header text="Cambiar contraseña de usuario" />
					{/* <Checkbox label="Autogenerar contraseña" style={{ marginTop: 10 }} checked={passwordForm && passwordForm.mode==PASSWORD_MODE.AUTOGENERATED} onChange={(v)=>setPasswordForm({ mode: v ? PASSWORD_MODE.AUTOGENERATED : PASSWORD_MODE.PROVIDED, password: passwordForm.password })} /> */}
					{passwordForm && passwordForm.mode==PASSWORD_MODE.PROVIDED ? (
						<Input label="Contraseña" style={{ marginTop: 10 }} value={passwordForm.password} onChange={onPassChange('password')}/>
					) : null}
					<Message centered type="info" style={{ marginTop: 10 }}>
						El sistema le enviará al usuario un correo para realizar un cambio de contraseña
						<br />
						<b>La contraseña actual del usuario dejará de funcionar</b>
					</Message>
					<Message type="error" list={modalPrompts} style={{ marginTop: 10 }} />
				</Modal.Content>
				<Modal.Actions>
					<Button text="Cancelar" basic onClick={bindClick(setShownModal)} />
					<Button text="Enviar" color="black" onClick={sendPasswordChange} />
				</Modal.Actions>
			</Modal>
		)}
		<Modal open={shownModal===Modals.PASSWORD_LINK} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Link de contraseña</Modal.Header>
			<Modal.Content>
				{passwordLink ? (
					<div>
						<Header size="small" text={'Restaurar contraseña'} subtext="Enlace para restaurar contraseña del usuario" />
						<Input readonly value={passwordLink} onFocus={(e)=>e.target.select()} buttons={<>
							<Button icon iconName="copy" onClick={()=>{
								navigator.clipboard.writeText(passwordLink).then(()=>{
									Toast.success(t('toasts.clipboard_success'))
								}, ()=>Toast.error(t('toasts.clipboard_error')))
							}} />
							<Button icon iconName="external-link" onClick={()=>window.open(passwordLink, '_blank')} />
						</>} />
					</div>
				) : (
					<Header loading={!modalPrompts || modalPrompts.length==0} text={modalPrompts ? t('common.error') : "Cargando enlace..."} subtext={modalPrompts ? modalPrompts[0] : null} />
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cerrar" onClick={bindClick(setShownModal)} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.CREATION_TABLE} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Tabla de creación</Modal.Header>
			<Modal.Content>
				<Table fitted details last>
					<Table.Body data={[
						['Fecha creación', moment.unix(user.date_created).format('DD/MMM/YY HH:mm')],
						['Usuario creador', userCreator ? (
							`[${userCreator.user_id}] ${userCreator.first_name} ${userCreator.last_name}`
						) : <>
							[{user.user_creator}]
							<div className="fr inline loading small" style={{ display: 'inline', marginLeft: 20 }}></div>
						</>],
					]} />
				</Table>
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cerrar" onClick={bindClick(setShownModal)} />
			</Modal.Actions>
		</Modal>

		{/* {canCreate && 
			<Modal open={shownModal===Modals.CLONE_USER} onClose={bindClick(setShownModal)} size="tiny">
				<Modal.Header>Clonar Usuario</Modal.Header>
				<Modal.Content>
					{ userCloneForm ? <div>
						<Input label={'Correo electrónico'} value={userCloneForm.email} onChange={onUserCloneChange('email')} />
						<Input label={'Nombre'} value={userCloneForm.first_name} onChange={onUserCloneChange('first_name')} />
						<Input label={'Apellido'} value={userCloneForm.last_name} onChange={onUserCloneChange('last_name')} />
						<Message text={'La contraseña del usuario se le enviará a el correo electrónico que se ingresó.'} type='info' style={{ marginTop: 15, marginBottom: 15 }} />
						<Message list={modalPrompts} type='error' />
					</div> : null }
				</Modal.Content>
				<Modal.Actions>
					<Button text="Cerrar" onClick={bindClick(setShownModal)} />
					<Button text='Guardar' color='black' onClick={saveClone}  />
				</Modal.Actions>
			</Modal>
		} */}
		
		{canRefresh && (
			<Modal open={shownModal===Modals.REFRESH_TOKEN} size='tiny' onClose={bindClick(setShownModal)}>
				<Modal.Header>Reiniciar Sesion</Modal.Header>
				<Modal.Content>
					<Header text='¿Reiniciar sesion del usuario?' style={{ marginBottom: 20 }} />
					<Message type='info' centered>
						Esto cerrará sesión en todos los dispositivos donde se inició sesión con este usuario. Se tendrá que acceder de nuevo con usuario y contraseña.
					</Message>
					<Checkbox checked={!!modalConfirm} onChange={bindClick(setModalConfirm, true)} label={'Deseo reiniciar la sesión'} style={{ marginTop: 20 }} />
					<Message list={modalPrompts} type='error' />
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
					<Button text='Continuar' color='black' disabled={!modalConfirm} onClick={sendReinitialize} />
				</Modal.Actions>
			</Modal>
		)}

		{canActivate && (
			<Modal open={shownModal===Modals.DEACTIVATE_USER} size="tiny" onClose={bindClick(setShownModal)}>
				<Modal.Header>{user.active ? 'Desactivar' : 'Activar'} usuario</Modal.Header>
				<Modal.Content>
					<Header text={`¿${user.access ? 'Desactivar' : 'Activar'} usuario?`} subtext={'Restringir usuario del sistema'} style={{ marginBottom: 20 }} />
					<Message type="error" centered>
						Esto desactivará la cuenta de usuario de cualquier sistema administrativo a la cual tenga acceso. El sistema cerrará la sesión del usuario de todos los dispositivos y perderá acceso al sistema.
					</Message>
					<Checkbox checked={!!modalConfirm} onChange={setModalConfirm} label={'Deseo reiniciar la sesión'} style={{ marginTop: 20 }} />
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
					<Button text='Continuar' color='black' disabled={!modalConfirm} onClick={sendActivate} />
				</Modal.Actions>
			</Modal>
		)}
		<Modal open={shownModal===Modals.GRANT_SYSTEM_ACCESS && !!editingSystem} size="tiny" onClose={bindClick(setShownModal)}>
			<Modal.Header>{!!systems[editingSystem] ? 'Quitar' : 'Dar'} acceso a usuario</Modal.Header>
			{!!editingSystem && (
				<Modal.Content>
					<Header text={`${!!systems[editingSystem] ? 'Quitar' : 'Dar'} acceso a ${SystemAccessRow.Types[editingSystem]?.name}`} />
					<Message centered type="info">
						{!!systems[editingSystem] ? <>
							Se le quitará el acceso a <b>{user.first_name} {user.last_name}</b> y ya no podrá entrar al sistema de <b>"{SystemAccessRow.Types[editingSystem]?.name}"</b>
						</> : <>
							Se le dará acceso a <b>{user.first_name} {user.last_name}</b> para entrar al sistema <b>"{SystemAccessRow.Types[editingSystem]?.name}"</b>
							<br />
							El usuario tendrá asignado quien le dio acceso para entrar a <b>"{SystemAccessRow.Types[editingSystem]?.name}"</b>
						</>}
					</Message>
					<Checkbox checked={!!modalConfirm} onChange={setModalConfirm} label={`Deseo ${!!systems[editingSystem] ? 'quitar' : 'dar'} este acceso`} style={{ marginTop: 20 }} />
					<Message list={modalPrompts} type="error" />
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Guardar" color="black" disabled={!modalConfirm} onClick={sendGrantAccess(editingSystem)} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.SYSTEM_ACCESS_INFO && !!editingSystem && !!systems[editingSystem]} size="mini" onClose={bindClick(setShownModal)}>
			<Modal.Header text="Información de acceso" />
			{!!editingSystem && <Modal.Content>
				<Table fitted details last>
					<Table.Body
						data={[
							['Sistema', SystemAccessRow.Types[editingSystem]?.name],
							['Fecha acceso', systems[editingSystem]?.date_added ? moment.unix(systems[editingSystem]?.date_added).format('YYYY-MM-DD HH:mm') : <Icon name="minus" />],
							['Usuario asignador', <>
								{systems[editingSystem]?.user_added_name ? (
									<Link to={`/users/${systems[editingSystem]?.user_added}`} target="_blank" style={{ padding: 0, color: '#4183c4' }}>
										[{systems[editingSystem]?.user_added}] {systems[editingSystem]?.user_added_name}
									</Link>
								) : <Icon name="minus" />}
							</>],
						]}
					/>
				</Table>
			</Modal.Content>}
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default UserDetails;